import React, { ReactNode } from "react";
import { UserProfile } from "common/graphql";

export const shouldShowInactiveNoticesPage = (userProfile: UserProfile | undefined | null): boolean => {
  return [
    process.env.REACT_APP_CLOH_ACCOUNT_ID,
    process.env.REACT_APP_DC_ACCOUNT_ID,
    process.env.REACT_APP_GRR_ACCOUNT_ID,
    process.env.REACT_APP_HOUSTON_ACCOUNT_ID,
    process.env.REACT_APP_JCNJ_ACCOUNT_ID,
    process.env.REACT_APP_LA_ACCOUNT_ID,
  ].includes(userProfile?.account?.id || "");
};

export const shouldShowInactiveCurbPassPage = (userProfile: UserProfile | undefined | null): boolean => {
  return [
    process.env.REACT_APP_ATLAIR_ACCOUNT_ID,
    process.env.REACT_APP_CLOH_ACCOUNT_ID,
    process.env.REACT_APP_DC_ACCOUNT_ID,
    process.env.REACT_APP_GRR_ACCOUNT_ID,
    process.env.REACT_APP_HBNJ_ACCOUNT_ID,
    process.env.REACT_APP_JCNJ_ACCOUNT_ID,
    process.env.REACT_APP_LA_ACCOUNT_ID,
    process.env.REACT_APP_MBPA_ACCOUNT_ID,
    process.env.REACT_APP_NYC_ACCOUNT_ID,
    process.env.REACT_APP_SMF_ACCOUNT_ID,
  ].includes(userProfile?.account?.id || "");
};

export interface NavigationLink {
  title: string;
  to: string;
  linkType?: "external";
  onClick?: (e: React.SyntheticEvent) => Promise<void>;
  icon?: ReactNode;
  hideIconOnMobile?: boolean;
}

export const getNavigationConfig = (
  userProfile: UserProfile | undefined | null,
  isMobile: boolean,
  logout: () => Promise<void>,
): NavigationLink[] => {
  if (!userProfile) {
    return [];
  }

  const accountLink: NavigationLink = {
    title: "Account",
    to: "/account",
  };

  const resourcesLink: NavigationLink = {
    title: "Resources",
    to: process.env.REACT_APP_CURBSUITE_SUPPORT_URL || "",
    linkType: "external",
  };

  const supportLink: NavigationLink = {
    title: "Support",
    to: process.env.REACT_APP_CURBSUITE_ZENDESK_SUPPORT_TICKET_URL || "",
    linkType: "external",
  };

  const logoutLink: NavigationLink = {
    title: "Logout",
    to: "/",
    onClick: async (e: React.SyntheticEvent) => {
      e.preventDefault();
      await logout();
    },
  };

  if (
    userProfile.account.id === process.env.REACT_APP_ATLAIR_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_BETHLEHEM_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_CLOH_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_DC_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_FORT_LEE_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_GRR_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_GSP_AIRPORT_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_HBNJ_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_HOUSTON_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_JCNJ_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_LA_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_MBPA_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_MPA_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_NYC_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_PHIL_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_PHILBUS_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_PITTSBURGH_ACCOUNT_ID ||
    userProfile.account.id === process.env.REACT_APP_SMF_ACCOUNT_ID
  ) {
    return [
      {
        title: "CurbPass",
        to:
          userProfile?.account?.id !== process.env.REACT_APP_PIKEVILLE_ACCOUNT_ID
            ? "/dashboard"
            : "/dashboard/citations",
      },
      {
        title: "Enforcement",
        to: "/notices",
      },
      { title: "Operations", to: "/operations" },
      { title: "Permits", to: "/permits" },
      {
        title: "Analytics",
        to: "/analytics",
      },
      ...(isMobile ? [supportLink, resourcesLink, accountLink] : []),
      logoutLink,
    ];
  } else {
    return [
      {
        title: "Transactions",
        to:
          userProfile?.account?.id !== process.env.REACT_APP_PIKEVILLE_ACCOUNT_ID
            ? "/dashboard"
            : "/dashboard/citations",
      },
      ...(userProfile?.account?.id === process.env.REACT_APP_PIKEVILLE_ACCOUNT_ID
        ? [{ title: "Scofflaws", to: "/scofflaws" }]
        : []),
      { title: "Operations", to: "/operations" },
      { title: "Permits", to: "/permits" },
      {
        title: "Analytics",
        to: "/analytics",
      },
      ...(isMobile ? [supportLink, resourcesLink, accountLink] : []),
      logoutLink,
    ];
  }
};

export interface AccountFeatures {
  invoicesEnabled: boolean;
  citationsEnabled: boolean;
  parkTransactionsEnabled: boolean;
  digitalWalletTransactionsEnabled: boolean;
  isBusStation: boolean;
  /**
   * Airport clients bill per trip -- financial.transaction rows of type "trip_charge", created from
   * LPR entry captures -- rather than per park session. They share the bus-station operator-billing
   * table layout, but differ in which transaction type backs it, which date field is authoritative,
   * and how the Type column reads. Kept separate from isBusStation so Philly Bus is unaffected.
   */
  isAirportTripCharges: boolean;
  notices: NoticeFeatures;
}

export interface NoticeFeatures {
  isTimeDisplayEnabledForPendingNotices: boolean;
}

export const getAccountFeatures = (userProfile: UserProfile | undefined | null): AccountFeatures => {
  if (!userProfile) {
    return {
      invoicesEnabled: false,
      citationsEnabled: false,
      parkTransactionsEnabled: false,
      digitalWalletTransactionsEnabled: false,
      isBusStation: false,
      isAirportTripCharges: false,
      notices: {
        isTimeDisplayEnabledForPendingNotices: false,
      },
    };
  }

  const ACCOUNTS_WITH_INVOICES_ENABLED = [
    process.env.REACT_APP_BETHLEHEM_ACCOUNT_ID,
    process.env.REACT_APP_HOUSTON_ACCOUNT_ID,
    process.env.REACT_APP_FORT_LEE_ACCOUNT_ID,
    process.env.REACT_APP_MPA_ACCOUNT_ID,
  ];

  const ACCOUNTS_WITH_CITATIONS_ENABLED = [process.env.REACT_APP_PIKEVILLE_ACCOUNT_ID];

  const invoicesEnabled = ACCOUNTS_WITH_INVOICES_ENABLED.includes(userProfile.account.id);
  const citationsEnabled = ACCOUNTS_WITH_CITATIONS_ENABLED.includes(userProfile.account.id);
  const parkTransactionsEnabled = userProfile.account.id !== process.env.REACT_APP_PIKEVILLE_ACCOUNT_ID;

  const payeeHasWallet = userProfile.account.wallets.length > 0;
  const digitalWalletTransactionsEnabled =
    payeeHasWallet && userProfile.account.wallets[0].accountId === userProfile.account.id;

  const ACCOUNTS_WITH_TIME_DISPLAYED_FOR_PENDING_NOTICES = [process.env.REACT_APP_SMF_ACCOUNT_ID];
  const isTimeDisplayEnabledForPendingNotices = ACCOUNTS_WITH_TIME_DISPLAYED_FOR_PENDING_NOTICES.includes(
    userProfile.account.id,
  );

  const BUS_STATION_ACCOUNTS = [process.env.REACT_APP_PHILBUS_ACCOUNT_ID];
  const isBusStation = BUS_STATION_ACCOUNTS.includes(userProfile.account.id);

  const AIRPORT_TRIP_CHARGE_ACCOUNTS = [process.env.REACT_APP_GSP_AIRPORT_ACCOUNT_ID];
  const isAirportTripCharges = AIRPORT_TRIP_CHARGE_ACCOUNTS.includes(userProfile.account.id);

  return {
    invoicesEnabled,
    citationsEnabled,
    parkTransactionsEnabled,
    digitalWalletTransactionsEnabled,
    isAirportTripCharges,
    isBusStation,
    notices: {
      isTimeDisplayEnabledForPendingNotices,
    },
  };
};
